import { Grid, Typography } from "@mui/material";

import { faChartBar, faChartScatter } from "@fortawesome/pro-solid-svg-icons";
import { DefaultPaper, CategoriesAvatar } from "@aclymatepackages/atoms";
import { formatDecimal } from "@aclymatepackages/formatters";
import { subcategoriesWithColors } from "@aclymatepackages/subcategories";
import { sumTonsCo2e } from "@aclymatepackages/other-helpers";

import ViewBarChart from "../../../../modules/charts/ViewBarChart";
import ViewScatterPlot from "../../../../modules/charts/ViewScatterPlot";
import EmissionsCustomTooltip from "../../../../modules/charts/EmissionsCustomTooltip";
import ViewGraphCardLayout from "../../../../layouts/ViewGraphCard";

import useDisplayMeasurementSystem from "../../../../../helpers/hooks/displayMeasurementSystem";

const VendorsScatterPlotCustomTooltip = ({ payload }) => {
  const { carbonUnitLabel } = useDisplayMeasurementSystem();

  const { name, tonsCo2e, avgTonsCo2ePerTransaction, transactionsCount } =
    payload[0]?.payload || {};

  const displayData = [
    {
      title: "Number of transactions",
      subtitle: transactionsCount,
    },
    {
      title: `Average ${carbonUnitLabel} per transaction`,
      subtitle: `${formatDecimal(
        avgTonsCo2ePerTransaction
      )} ${carbonUnitLabel}`,
    },
  ];

  return (
    <DefaultPaper>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h6">{name}</Typography>
          <Typography
            variant="subtitle2"
            color="textSecondary"
          >{`${formatDecimal(
            tonsCo2e
          )} total ${carbonUnitLabel} emitted (Bubble Size)`}</Typography>
        </Grid>
        {displayData.map(({ title, subtitle }, idx) => (
          <Grid item key={`tooltip-display-data-${idx}`}>
            <Typography variant="subtitle1">{title}</Typography>
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </DefaultPaper>
  );
};

const VendorsBarChart = ({ dataArray, barAreasArray, setSelectedObject }) => {
  const { convertCarbonUnits } = useDisplayMeasurementSystem();

  const formattedVendors = dataArray.map((vendor) => {
    const {
      transactions,
      name,
      id,
      tonsCo2e,
      totalTonsCo2ePercentage,
      typeTonsCo2ePercentage,
    } = vendor;

    const uniqueTransactionSubcategories = [
      ...new Set(
        transactions
          .filter(({ tonsCo2e }) => tonsCo2e)
          .map(({ subcategory }) => subcategory)
      ),
    ];

    const tonsCo2ePerSubcategory = uniqueTransactionSubcategories.map(
      (subcategory) => {
        const subcategoryTransactions = transactions.filter(
          (transaction) => transaction.subcategory === subcategory
        );
        return { subcategory, tonsCo2e: sumTonsCo2e(subcategoryTransactions) };
      }
    );

    const subcategoryVolumePropsObj = Object.fromEntries(
      tonsCo2ePerSubcategory.map(({ subcategory, tonsCo2e }) => [
        `${subcategory}TonsCo2e`,
        convertCarbonUnits(tonsCo2e),
      ])
    );

    return {
      id,
      emissionsSumTons: convertCarbonUnits(tonsCo2e),
      label: name,
      rowAnnotation: `(${formatDecimal(
        totalTonsCo2ePercentage
      )}% of total emissions)`,
      totalTonsCo2ePercentage,
      typeTonsCo2ePercentage,
      ...subcategoryVolumePropsObj,
    };
  });

  return (
    <ViewBarChart
      dataType="vendors"
      setSelectedObject={setSelectedObject}
      data={formattedVendors}
      barAreasArray={barAreasArray}
      tooltipComponent={
        <EmissionsCustomTooltip categoriesArray={barAreasArray} />
      }
      showBrushAnnotation
    />
  );
};

const VendorsScatterPlot = ({ dataArray, setSelectedObject }) => {
  const { carbonUnitLabel, convertCarbonUnits } = useDisplayMeasurementSystem();

  const convertedDataArray = dataArray.map(
    ({ avgTonsCo2ePerTransaction, tonsCo2e, ...otherProps }) => ({
      ...otherProps,
      avgTonsCo2ePerTransaction: convertCarbonUnits(avgTonsCo2ePerTransaction),
      tonsCo2e: convertCarbonUnits(tonsCo2e),
    })
  );

  return (
    <ViewScatterPlot
      setSelectedObject={setSelectedObject}
      dataArray={convertedDataArray}
      yKey="transactionsCount"
      yName="Number of transactions"
      yLabel="Number of transactions"
      xKey="avgTonsCo2ePerTransaction"
      xName={`Average ${carbonUnitLabel} Per Transaction`}
      xLabel={`Average ${carbonUnitLabel} Per Transaction`}
      zKey="tonsCo2e"
      zName={`Total ${carbonUnitLabel} CO2 Emitted (bubble size)`}
      tooltipComponent={<VendorsScatterPlotCustomTooltip type="vendors" />}
      name="Vendors"
      color="vendors"
    />
  );
};

const VendorsGraphs = ({ vendors, setSelectedVendor }) => {
  const barAreasArray = subcategoriesWithColors.map(
    ({ subcategory, name, icon, color }) => {
      const dataKey = `${subcategory}TonsCo2e`;

      return {
        icon,
        color,
        subcategory: dataKey,
        dataKey,
        name,
        customAvatar: <CategoriesAvatar subcategory={subcategory} />,
      };
    }
  );

  const graphs = [
    {
      title: "Total Emissions Per Vendor",
      type: "bar",
      icon: faChartBar,
      Graph: VendorsBarChart,
      graphProps: {
        barAreasArray,
        setSelectedObject: setSelectedVendor,
      },
      sortable: true,
    },
    {
      title:
        "Number of transactions vs. Average Tonnes CO2 emitted per transaction",
      type: "scatter",
      icon: faChartScatter,
      Graph: VendorsScatterPlot,
      graphProps: { setSelectedObject: setSelectedVendor },
    },
  ];

  return (
    <ViewGraphCardLayout
      subtitle="NOTE: We only show confirmed vendors with transactions"
      graphs={graphs}
      color="vendors"
      dataArray={vendors}
      analyticsCta="to see how each of your vendors contributes to your carbon footprint"
    />
  );
};
export default VendorsGraphs;
